import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { carrierApi } from '../../firebase/functionsService';
import { EMPTY_INVOICE_PROFILE, normalizeInvoiceProfile, validateInvoiceProfile } from '../../utils/invoiceProfile';
import Field from './Field';
import { ui } from './carrierStyles';

// Firmaets fakturaopplysninger (utsender på fakturaer). Lagres i organizations/{orgId}/private/invoiceProfile
// via saveCarrierInvoiceProfile. Bare admin leser og skriver.
//   defaults: { legalName, orgNumber } fra org-dokumentet, brukes til tomme felt
export default function CarrierInvoiceProfileForm({ orgId, defaults }) {
  const [form, setForm] = useState({ ...EMPTY_INVOICE_PROFILE, ...(defaults ?? {}) });
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, 'organizations', orgId, 'private', 'invoiceProfile'))
      .then(snap => {
        if (cancelled || !snap.exists()) return;
        const saved = snap.data();
        setForm({ ...EMPTY_INVOICE_PROFILE, ...saved, invoiceAddress: { ...EMPTY_INVOICE_PROFILE.invoiceAddress, ...(saved.invoiceAddress ?? {}) } });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [orgId]);

  const edited = () => setStatus(s => (s === 'saving' ? s : 'idle'));
  const set = (field) => (e) => { edited(); setForm(f => ({ ...f, [field]: e.target.value })); };
  const setAddr = (field) => (e) => { edited(); setForm(f => ({ ...f, invoiceAddress: { ...f.invoiceAddress, [field]: e.target.value } })); };

  async function submit(e) {
    e.preventDefault();
    const found = validateInvoiceProfile(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setStatus('saving');
    try {
      await carrierApi.saveCarrierInvoiceProfile({ orgId, profile: normalizeInvoiceProfile(form) });
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  }

  if (!loaded) return <p style={ui.hint}>Laster…</p>;

  return (
    <form onSubmit={submit} noValidate>
      <Field id="ci-name" label="Firmanavn" error={errors.legalName}>
        <input id="ci-name" style={ui.input} value={form.legalName} onChange={set('legalName')} maxLength={120} />
      </Field>
      <Field id="ci-org" label="Organisasjonsnummer" error={errors.orgNumber}>
        <input id="ci-org" style={ui.input} value={form.orgNumber} onChange={set('orgNumber')} inputMode="numeric" />
      </Field>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, fontSize: 14 }}>
        <input type="checkbox" checked={form.mvaRegistered} onChange={e => { edited(); setForm(f => ({ ...f, mvaRegistered: e.target.checked })); }} />
        <span>Registrert i mva-registeret</span>
      </label>
      <Field id="ci-account" label="Kontonummer for utbetaling" error={errors.bankAccount}>
        <input id="ci-account" style={ui.input} value={form.bankAccount} onChange={set('bankAccount')} inputMode="numeric" />
      </Field>
      <Field id="ci-street" label="Gateadresse" error={errors.street}>
        <input id="ci-street" style={ui.input} value={form.invoiceAddress.street} onChange={setAddr('street')} maxLength={120} />
      </Field>
      <div style={ui.row}>
        <Field id="ci-postal" label="Postnummer" error={errors.postalCode} style={{ flex: '0 0 120px' }}>
          <input id="ci-postal" style={ui.input} value={form.invoiceAddress.postalCode} onChange={setAddr('postalCode')} inputMode="numeric" maxLength={4} />
        </Field>
        <Field id="ci-city" label="Poststed" error={errors.city} style={{ flex: 1, minWidth: 160 }}>
          <input id="ci-city" style={ui.input} value={form.invoiceAddress.city} onChange={setAddr('city')} maxLength={80} />
        </Field>
      </div>
      {status === 'error' && <p role="alert" style={ui.error}>Kunne ikke lagre. Prøv igjen.</p>}
      {status === 'saved' && <p role="status" style={ui.ok}>Lagret</p>}
      <button type="submit" className="tippi-btn" style={{ ...ui.btn, marginTop: 14 }} disabled={status === 'saving'}>
        {status === 'saving' ? 'Lagrer…' : 'Lagre fakturaopplysninger'}
      </button>
    </form>
  );
}
